/**
 * Read-only hybrid preview — the "one diff, two renderers" preview side.
 *
 * Runs the SAME read prefix + pure plan as `applyViaHybridEngine` (read the live paragraph, extract
 * fields, build the op plan), but MUTATES NOTHING and renders the resulting ops to track-changes
 * HTML. Because it diffs the same live baseline against the same target and produces the same `Op[]`,
 * the preview is exactly what a subsequent Submit will apply (WYSIWYG).
 *
 * `Word` is referenced only inside the function, so importing this never breaks the Vitest env.
 */

import { buildApplyPlan } from './buildApplyPlan';
import { createProtectedRegionSet } from './protectedRegions';
import { extractProtectedRegions } from './extractProtectedRegions';
import { extractRevisionsForNormalizedBaseline } from './extractRevisions';
import { opsToTrackChangesHtml } from './opsToTrackChangesHtml';
import { debugHybridPipeline } from './debugPipeline';
import { Op, RevisionContext } from './types';
import { logQuickResponseStage, qrTextSnapshot } from '../../utils/quickResponseDebug';
import { normalizeWordParagraphBaseline } from '../../utils/bidiText';

/* global Word */

export interface HybridPreview {
  /** The op model (baseline order) — identical to what the apply will execute. */
  ops: Op[];
  /** Track-changes HTML for the preview card. */
  html: string;
  baselineLength: number;
  targetLength: number;
}

/**
 * Compute the hybrid redline for a paragraph without touching the document.
 *
 * @param paragraphIndex 0-based paragraph index
 * @param cleanTarget    the AI/manual clean accepted-state target
 * @returns the preview, or null if the paragraph can't be resolved / an error occurs
 */
export async function previewHybridOps(
  paragraphIndex: number,
  cleanTarget: string,
  options: { revisionContext?: RevisionContext } = {},
): Promise<HybridPreview | null> {
  try {
    return await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load('items');
      await context.sync();

      const rawParagraph = paragraphs.items[paragraphIndex];
      if (!rawParagraph) return null;
      const paragraph = rawParagraph as unknown as Word.Paragraph;

      const whole = paragraph.getRange('Whole');
      whole.load('text');
      await context.sync();
      const rawBaseline = whole.text || '';
      const baselineVisible = normalizeWordParagraphBaseline(rawBaseline);

      // Fields only block edits (same as apply); read-only extraction.
      const extracted = await extractProtectedRegions(context, paragraph);
      const protectedSet = createProtectedRegionSet(
        extracted.filter((e) => e.region.kind === 'field').map((e) => e.region),
      );

      // Same existing-revision context the apply uses, so the preview matches the apply.
      const revisionContext =
        options.revisionContext ??
        (await extractRevisionsForNormalizedBaseline(
          context,
          paragraph,
          rawBaseline,
          baselineVisible,
        ));

      const plan = buildApplyPlan(baselineVisible, cleanTarget, protectedSet, revisionContext);
      debugHybridPipeline('preview', baselineVisible, cleanTarget, revisionContext, plan.targetVisible, plan.ops);
      logQuickResponseStage('previewHybrid: plan built', {
        paragraphIndex,
        baselineVisible: qrTextSnapshot('baselineVisible', baselineVisible),
        cleanTarget: qrTextSnapshot('cleanTarget', cleanTarget),
        insertOps: plan.ops.filter((o) => o.kind === 'insert').map((o) => o.value.slice(-80)),
      });
      return {
        ops: plan.ops,
        // Render existing revisions in their colour (theirs=red / ours=blue) so the preview matches
        // the author-aware view and the flicker to a "wrong" state is gone.
        html: opsToTrackChangesHtml(plan.ops, revisionContext),
        baselineLength: baselineVisible.length,
        targetLength: plan.targetVisible.length,
      };
    });
  } catch (err) {
    logQuickResponseStage('previewHybrid FAILED', {
      paragraphIndex,
      error: err instanceof Error ? err.message : String(err),
      cleanTarget: qrTextSnapshot('cleanTarget', cleanTarget),
    });
    return null;
  }
}
